<script>
import { format, parseISO } from 'date-fns';
import { required, url } from '@vuelidate/validators';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import HelperTextPopup from 'dashboard/components/ui/HelperTextPopup.vue';
import { isValidURL } from '../helper/URLHelper';
import { getRegexp } from 'shared/helpers/Validators';
import { useVuelidate } from '@vuelidate/core';
import { emitter } from 'shared/helpers/mitt';

import NextButton from 'dashboard/components-next/button/Button.vue';

const DATE_FORMAT = 'yyyy-MM-dd';

export default {
  components: {
    MultiselectDropdown,
    HelperTextPopup,
    NextButton,
  },
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: '' },
    description: { type: String, default: '' },
    values: { type: Array, default: () => [] },
    value: { type: [String, Number, Boolean], default: '' },
    showActions: { type: Boolean, default: false },
    attributeType: { type: String, default: 'text' },
    attributeRegex: {
      type: String,
      default: null,
    },
    regexCue: { type: String, default: null },
    attributeKey: { type: String, required: true },
    contactId: { type: Number, default: null },
  },
  emits: ['update', 'delete', 'copy'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isEditing: false,
      editedValue: null,
    };
  },
  computed: {
    displayValue() {
      if (this.isAttributeTypeDate) {
        return this.value
          ? new Date(this.value || new Date()).toLocaleDateString()
          : '---';
      }
      if (this.isAttributeTypeCheckbox) {
        return this.value === 'false' ? false : this.value;
      }
      return this.hasValue ? this.value : '---';
    },
    formattedValue() {
      return this.isAttributeTypeDate
        ? format(this.value ? new Date(this.value) : new Date(), DATE_FORMAT)
        : this.value;
    },
    listOptions() {
      return this.values.map((value, index) => ({
        id: index + 1,
        name: value,
      }));
    },
    selectedItem() {
      const id = this.values.indexOf(this.editedValue) + 1;
      return { id, name: this.editedValue };
    },
    isAttributeTypeCheckbox() {
      return this.attributeType === 'checkbox';
    },
    isAttributeTypeList() {
      return this.attributeType === 'list';
    },
    isAttributeTypeLink() {
      return this.attributeType === 'link';
    },
    isAttributeTypeDate() {
      return this.attributeType === 'date';
    },
    hasValue() {
      return this.value !== null && this.value !== '';
    },
    // SITEUP: chave técnica do WhatsApp (JID/LID/Chat ID) — mono apagado,
    // valor se copia, não se lê
    isTechnicalAttribute() {
      return String(this.attributeKey || '').startsWith('whatsapp_');
    },
    urlValue() {
      return isValidURL(this.value) ? this.value : '---';
    },
    hrefURL() {
      return isValidURL(this.value) ? this.value : '';
    },
    notAttributeTypeCheckboxAndList() {
      return !this.isAttributeTypeCheckbox && !this.isAttributeTypeList;
    },
    inputType() {
      return this.isAttributeTypeLink ? 'url' : this.attributeType;
    },
    shouldShowErrorMessage() {
      return this.v$.editedValue.$error;
    },
    errorMessage() {
      if (this.v$.editedValue.url?.$invalid) {
        return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_URL');
      }
      if (this.v$.editedValue.regexValidation?.$invalid) {
        return (
          this.regexCue ||
          this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_INPUT')
        );
      }
      return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.REQUIRED');
    },
  },
  watch: {
    value() {
      this.isEditing = false;
      this.editedValue = this.formattedValue;
    },
    contactId() {
      // Fix to solve validation not resetting when contactId changes in contact page
      this.v$.$reset();
    },
  },

  validations() {
    if (this.isAttributeTypeLink) {
      return {
        editedValue: { required, url },
      };
    }
    return {
      editedValue: {
        required,
        regexValidation: value => {
          if (!this.attributeRegex || !value) return true;
          try {
            return getRegexp(this.attributeRegex).test(value);
          } catch {
            return false;
          }
        },
      },
    };
  },
  mounted() {
    this.editedValue = this.formattedValue;
    emitter.on(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  methods: {
    onFocusAttribute(focusAttributeKey) {
      if (this.attributeKey === focusAttributeKey) {
        this.onEdit();
      }
    },
    focusInput() {
      if (this.$refs.inputfield) {
        this.$refs.inputfield.focus();
      }
    },
    onClickAway() {
      this.v$.$reset();
      this.isEditing = false;
    },
    onEdit() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    onUpdateListValue(value) {
      if (value) {
        this.editedValue = value.name;
        this.onUpdate();
      }
    },
    onUpdate() {
      const updatedValue =
        this.attributeType === 'date'
          ? parseISO(this.editedValue)
          : this.editedValue;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.isEditing = false;
      this.$emit('update', this.attributeKey, updatedValue);
    },
    onDelete() {
      this.isEditing = false;
      this.v$.$reset();
      this.$emit('delete', this.attributeKey);
    },
    onCopy() {
      this.$emit('copy', this.value);
    },
  },
};
</script>

<template>
  <!-- SITEUP V2 / G2: linha kv densa (rótulo 12.5 muted · valor 13.5) -->
  <div class="group/attr mb-2 py-0.5 last:mb-0">
    <div class="flex items-start justify-between gap-2.5">
      <div class="flex min-w-0 flex-1 items-start gap-2">
        <div
          v-if="isAttributeTypeCheckbox"
          class="flex shrink-0 items-center pt-0.5"
        >
          <input
            v-model="editedValue"
            class="!my-0"
            type="checkbox"
            @change="onUpdate"
          />
        </div>
        <span
          class="inline-flex min-w-[62px] max-w-[40%] shrink-0 items-center gap-1 text-[12.5px] leading-snug"
          :class="v$.editedValue.$error ? 'text-n-ruby-11' : 'text-n-slate-11'"
        >
          <span
            v-if="icon"
            :class="icon"
            class="size-3.5 flex-shrink-0 text-n-slate-11"
          />
          <span class="truncate" :title="label">{{ label }}</span>
          <HelperTextPopup
            v-if="description"
            :message="description"
            class="mt-0.5"
          />
        </span>

        <div v-if="notAttributeTypeCheckboxAndList" class="min-w-0 flex-1">
          <div v-if="isEditing" v-on-clickaway="onClickAway">
            <div class="mb-1 flex w-full items-center">
              <input
                ref="inputfield"
                v-model="editedValue"
                :type="inputType"
                class="!mb-0 !h-8 !text-[13.5px] ltr:!rounded-r-none rtl:!rounded-l-none"
                autofocus="true"
                :class="{ error: v$.editedValue.$error }"
                @blur="v$.editedValue.$touch"
                @keyup.enter="onUpdate"
              />
              <div>
                <NextButton
                  sm
                  icon="i-ri-check-line"
                  class="h-[34px] ltr:rounded-l-none rtl:rounded-r-none"
                  @click="onUpdate"
                />
              </div>
            </div>
            <span
              v-if="shouldShowErrorMessage"
              class="-mt-px block w-full text-sm font-normal text-n-ruby-11"
            >
              {{ errorMessage }}
            </span>
          </div>
          <div
            v-show="!isEditing"
            class="flex items-start justify-end gap-1 text-right"
            :class="{ 'is-editable': showActions }"
          >
            <a
              v-if="isAttributeTypeLink"
              :href="hrefURL"
              target="_blank"
              rel="noopener noreferrer"
              :title="urlValue"
              class="mb-0 min-w-0 truncate text-[13.5px] font-medium text-n-slate-12 hover:underline"
            >
              {{ urlValue }}
            </a>
            <p
              v-else
              :title="String(displayValue)"
              class="mb-0 min-w-0 truncate font-medium"
              :class="
                isTechnicalAttribute
                  ? 'font-mono text-[12px] text-n-slate-11'
                  : 'text-[13.5px] text-n-slate-12'
              "
            >
              {{ displayValue }}
            </p>
            <div class="flex shrink-0 items-center gap-0.5">
              <NextButton
                v-if="showActions && hasValue"
                v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
                xs
                slate
                ghost
                icon="i-ri-clipboard-line"
                class="attr-copy-action hidden flex-shrink-0 group-hover/attr:flex"
                @click="onCopy"
              />
              <NextButton
                v-if="showActions"
                v-tooltip.right="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
                xs
                slate
                ghost
                icon="i-ri-ball-pen-line"
                class="hidden flex-shrink-0 group-hover/attr:flex"
                @click="onEdit"
              />
            </div>
          </div>
        </div>

        <div v-if="isAttributeTypeList" class="min-w-0 flex-1">
          <MultiselectDropdown
            :options="listOptions"
            :selected-item="selectedItem"
            :has-thumbnail="false"
            :multiselector-placeholder="
              $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.PLACEHOLDER')
            "
            :no-search-result="
              $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
            "
            :input-placeholder="
              $t(
                'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
              )
            "
            @select="onUpdateListValue"
          />
        </div>
      </div>
      <NextButton
        v-if="showActions && hasValue"
        v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
        slate
        sm
        link
        icon="i-ri-delete-bin-line"
        class="opacity-0 transition-opacity group-hover/attr:opacity-100"
        @click="onDelete"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.selector-wrap) {
  @apply m-0 top-1;

  .selector-name {
    @apply ml-0;
  }
}

:deep(.name) {
  @apply ml-0;
}

/* No toque não existe hover: o copiar fica sempre visível */
@media (hover: none) {
  .attr-copy-action {
    display: flex !important;
  }
}
</style>
